import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DownloadIcon, ClockIcon, CalendarIcon, MapPinIcon } from "@/components/Icons";
import { toast } from "sonner";

export default function MyBookings() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/bookings/mine")
            .then((r) => setItems(r.data))
            .finally(() => setLoading(false));
    }, []);

    const download = async (b) => {
        try {
            const token = localStorage.getItem("es_token");
            const res = await fetch(`${API}/bookings/${b.id}/admit-card`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) throw new Error("Failed");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `admit-card-${b.booking_code}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error("Unable to download admit card");
        }
    };

    return (
        <div className="mx-auto max-w-6xl px-6 py-10">
            <div className="overline mb-3">Your reservations</div>
            <h1 className="font-heading text-4xl font-bold tracking-tight">My bookings</h1>

            {loading ? (
                <div className="mt-6 text-muted-foreground">Loading…</div>
            ) : items.length === 0 ? (
                <div className="mt-8 border border-dashed border-border p-10 text-center bg-card" data-testid="empty-bookings">
                    <div className="font-heading text-lg font-bold">No bookings yet</div>
                    <p className="mt-1 text-sm text-muted-foreground">Head over to the <a className="text-primary underline" href="/centers">centers directory</a> to book your first slot.</p>
                </div>
            ) : (
                <div className="mt-8 space-y-3">
                    {items.map((b) => (
                        <div key={b.id} className="border border-border bg-card grid md:grid-cols-12 gap-4 p-5 items-center" data-testid={`booking-${b.id}`}>
                            <div className="md:col-span-2">
                                <div className="overline">Code</div>
                                <div className="font-mono font-bold text-lg">{b.booking_code}</div>
                                <StatusBadge status={b.status} paid={b.payment_status} />
                            </div>
                            <div className="md:col-span-5">
                                <div className="font-heading text-lg font-bold">{b.exam_category}</div>
                                <div className="mt-1 text-sm text-muted-foreground flex items-center gap-1"><MapPinIcon className="h-3.5 w-3.5" />{b.center_name}</div>
                            </div>
                            <div className="md:col-span-3 text-sm">
                                <div className="flex items-center gap-1 text-muted-foreground"><CalendarIcon className="h-3.5 w-3.5" />{b.exam_date}</div>
                                <div className="flex items-center gap-1 text-muted-foreground mt-1"><ClockIcon className="h-3.5 w-3.5" />{b.exam_start_time} — {b.exam_end_time}</div>
                                <div className="font-mono text-xs mt-1">Seat S-{String(b.seat_number).padStart(3, "0")}</div>
                            </div>
                            <div className="md:col-span-2 flex md:justify-end">
                                <Button
                                    onClick={() => download(b)}
                                    disabled={b.status !== "confirmed"}
                                    className="rounded-sm bg-primary hover:bg-primary/90"
                                    data-testid={`download-${b.id}`}
                                >
                                    <DownloadIcon className="h-4 w-4 mr-2" /> Admit Card
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function StatusBadge({ status, paid }) {
    const label = status === "confirmed" ? "CONFIRMED" : status === "cancelled" ? "CANCELLED" : "PENDING";
    const color = status === "confirmed" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                : status === "cancelled" ? "text-red-700 bg-red-50 border-red-200"
                : "text-amber-700 bg-amber-50 border-amber-200";
    return (
        <div className="mt-2 flex flex-col gap-1">
            <span className={`inline-block text-[10px] tracking-widest border px-1.5 py-0.5 ${color}`}>{label}</span>
            {paid === "paid" && <span className="text-[10px] tracking-widest text-emerald-700">PAID</span>}
            {paid === "pending" && status !== "confirmed" && <span className="text-[10px] tracking-widest text-amber-700">PAYMENT DUE</span>}
        </div>
    );
}
